import {Box, Skeleton, Stack, Typography, useTheme} from '@mui/material';
import {useQueryClient} from '@tanstack/react-query';
import React, {Fragment, Suspense, useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';

import {GetFiveStarsModel} from 'domain/usecases';
import {WhoIndicatesTagParametricEnum} from 'domain/usecases/private/home/who-indicates';
import {QUERY_KEYS} from 'infra/libs/react-query/queryKeys';
import {LoginEnum} from 'main/analytics/gaTagsEnum';
import {
	IFrameModal,
	MainFooter,
	MainHeader,
	PageWrapperLayout,
	SearchBar,
} from 'presentation/components';
import {ConciergeDrawer} from 'presentation/components/ConciergeDrawer';
import {useConciergeDrawerStore} from 'presentation/components/ConciergeDrawer/store';
import {StyledGrid} from 'presentation/components/Grid';
import {FullscreenLoading} from 'presentation/components/Loading/FullscreenLoading';
import {ModalError} from 'presentation/components/ModalError';
import {HEALTH_CARD_MENU_ITEM_ID} from 'presentation/components/Navigation/constants';
import {OurDoctorHomeDrawer} from 'presentation/components/OurDoctor/HomeDrawer';
import {UserManualAlert} from 'presentation/components/UserManualAlert';
import {WhoIndicatesCard} from 'presentation/components/WhoIndicatesCard';
import {
	CommunicationsEnum,
	ContractTypeEnum,
	FeaturesEnum,
	HealthCardEnum,
	ServicesEnum,
} from 'presentation/constants';
import {useContractStatus, useHostName} from 'presentation/hooks';
import {useClarity} from 'presentation/hooks/useClarity';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {useAccountDataStore} from 'presentation/pages/private/store/useAccountDataStateStore';
import {useGetOurDoctorMessageQuery} from 'presentation/queries/useGetOurDoctorMessageQuery';
import {translations} from 'presentation/translations-new';
import {useCommunications} from '../communications/hooks/useCommunications';
import {FiveStars} from '../five-stars';
import {useMobileErrorModalStore} from '../five-stars/stores/useMobileErrorModalStore';
import {useMobileRefuseModalStore} from '../five-stars/stores/useMobileRefuseModalStore';
import {useRatingModalStore} from '../five-stars/stores/useRatingModalStore';
import {useAccountInformation, useMediaQueries} from '../hooks';
import {useMyHospitalizationFeatureFlag} from '../hooks/useMyHospitalizationFeatureFlag';
import {useConcierge} from '../my-hospitalization/hooks/useConcierge';
import {usePatientData} from '../my-hospitalization/pages/PatientData/hooks/usePatientData';
import {
	Banners,
	BannersV2,
	DebtSettlementStatement,
	Financial,
	NextAppointments,
	RegistrationUpdateSurface,
	RemindersAndNotifications,
	SectionContainer,
} from './components';
import {BlogBanners} from './components/BlogBanner';
import {Concierge} from './components/Concierge';
import {MyHospitalizationAlert} from './components/MyHospitalizationAlert';
import {useTelemedicineItems} from './components/OptionsDrawer/hooks/useTelemedicineItems';
import {ShortcutList} from './components/ShortcutList';
import {WaitingPeriodTutorial} from './components/WaitingPeriodTutorial';
import {YouTubeBanners} from './components/YouTubeBanner';
import {useHomeGeolocationPrompt} from './hooks/useHomeGeolocationPrompt';
import {ElectronicBillSection} from './components/ElectronicBillSection';
import {CongenereDrawer} from './components/CongenereDrawer';
import {OdontoTermDrawer} from './components/OdontoTermDrawer';
import {useOdontoTermDrawerVisibility} from './components/OdontoTermDrawer/hooks/useOdontoTermDrawerVisibility';
import {StyledBox, StyledPortalHomeCard} from './styles';

const {searchService} = translations['pt-br'].privateFlow.services;
const {loading, referAndEarn, seeAll} = translations['pt-br'].commons;
const {title, quickAccess} = translations['pt-br'].privateFlow.home;
const {title: conciergeTitle} = translations['pt-br'].privateFlow.home.concierge;
const {
	title: modalErrorTitle,
	subtitle,
	description,
	tryAgainBtn,
	backAction,
} = translations['pt-br'].others.genericErrorModal;

export function Home() {
	const [isFavoritesShortcutTutorialActive, setIsFavoritesShortcutTutorialActive] = useState(
		() => !localStorage.getItem('favoritesShortcut'),
	);
	const {handleClarityEvent, clarityEvent} = useClarity();
	handleClarityEvent(clarityEvent.VIEW_HOME_PAGE);
	const {isLaptop, isMobile, isTablet, isDesktop} = useMediaQueries();
	const {isNotredameSaude, isGndiMinas} = useHostName();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const theme = useTheme();
	const {
		isError: isTelemedicineError,
		setIsError: setIsTelemedicineError,
		handleEmergencyTelehealthClick,
	} = useTelemedicineItems();
	useHomeGeolocationPrompt();

	const {isEnabled: conciergeEnabled} = useFeatureFlag(FeaturesEnum.PORTAL_FEATURE_CONCIERGE);
	const {isEnabled: contentManagerEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_CONTENT_MANAGER,
	);
	const {isEnabled: ourDoctorFlagIsEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_NOSSO_MEDICO,
	);
	const {isEnabled: bannerHomeEnabled} = useFeatureFlag(FeaturesEnum.PORTAL_FEATURE_BANNER_HOME);
	const {isEnabled: bannerVtexEnabled} = useFeatureFlag(FeaturesEnum.PORTAL_FEATURE_BANNER_VTEX);
	const {isEnabled: electronicBillAlertEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_ELETRONIC_BILL_ALERT,
	);
	const {isEnabled: myHospitalizationEnabled} = useMyHospitalizationFeatureFlag();
	const {data: hospitalizationPatientData} = usePatientData();

	const {
		beneficiary: {tipoContratacao},
		sidebar: {flPad},
	} = useAccountDataStore(store => store.accountData);
	const {
		isHolder,
		isResponsibleWithoutBenefits,
		isB2BAccess,
		accessConcierge,
		isOurDoctorPlan,
		isEarlyAccess,
		isInGracePeriod,
		isWithinEnrollmentPeriod,
	} = useAccountInformation();
	const {isValid, isCancelledLessThanThreeMonths, isCancelledAtFiveYears} = useContractStatus();

	const showConcierge =
		!isResponsibleWithoutBenefits && isValid && conciergeEnabled && accessConcierge;
	const showMyHospitalizationAlert =
		!isResponsibleWithoutBenefits &&
		isValid &&
		!!hospitalizationPatientData &&
		myHospitalizationEnabled;

	const {openConciergeIframe, setOpenConciergeIframe, handleCloseIframe, conciergeUrl} =
		useConcierge();
	const isConciergeDrawerOpen = useConciergeDrawerStore(state => state.isOpen);
	const {isOpen: isOdontoTermDrawerOpen} = useOdontoTermDrawerVisibility();
	const ratingModal = useRatingModalStore(state => state.ratingModal);
	const mobileRefuseModal = useMobileRefuseModalStore(state => state.refuseModal);
	const mobileErrorModal = useMobileErrorModalStore(state => state.errorModal);
	const fiveStarsRatings = queryClient.getQueryData<GetFiveStarsModel>([QUERY_KEYS.FIVE_STARS]);

	const {
		communicationsResponse,
		getCommunicationsQuery: {isLoading},
		confirmCommunicationHasError,
	} = useCommunications();

	const {isEnabled: isQuickWinWaitingPeriodFlagEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_QUICK_WIN_WAITING_PERIOD,
	);

	const {isEnabled: isHideRecommendationFlagEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_HIDE_RECOMMENDATION,
	);

	const BUSINESS_PLAN = tipoContratacao === ContractTypeEnum.EMPRESARIAL && !flPad;

	const showFinancial =
		!BUSINESS_PLAN &&
		(isValid || (!isValid && isResponsibleWithoutBenefits && isCancelledAtFiveYears));

	const showDebtSettlementStatement = isCancelledLessThanThreeMonths;

	const isAbleToCallCommunications = isHolder && !confirmCommunicationHasError;
	const isLoadingCommunications = isAbleToCallCommunications && isLoading;

	const hideWhoIndicatesToNotredameSaude = isNotredameSaude && isHideRecommendationFlagEnabled;
	const showWhoIndicates = !isB2BAccess && isValid && !hideWhoIndicatesToNotredameSaude;

	const {data: alreadyViewedOurDoctorMessageData, isFetching: isOurDoctorMessageLoading} =
		useGetOurDoctorMessageQuery({enabled: isOurDoctorPlan && ourDoctorFlagIsEnabled});

	const showLoading = isLoadingCommunications || isOurDoctorMessageLoading;

	const [isWaitingPeriodTutorialActive, setIsWaitingPeriodTutorialActive] = useState(
		() => !localStorage.getItem(HEALTH_CARD_MENU_ITEM_ID),
	);

	const bannerComponent = bannerVtexEnabled ? <BannersV2 /> : <Banners />;

	if (showLoading) {
		return <FullscreenLoading text={loading} />;
	}

	if (isAbleToCallCommunications && communicationsResponse?.length > 0) {
		navigate(CommunicationsEnum.COMMUNICATIONS);
	}

	const shouldOpenOurDoctorDrawer =
		ourDoctorFlagIsEnabled &&
		isOurDoctorPlan &&
		alreadyViewedOurDoctorMessageData &&
		alreadyViewedOurDoctorMessageData?.visualizou === false;

	const hideFiveStars = shouldOpenOurDoctorDrawer;
	const isFiveStarsModalOpen =
		mobileRefuseModal || mobileErrorModal || (!!fiveStarsRatings && ratingModal !== 'stale');
	const isAnyHomeModalOpen =
		isTelemedicineError ||
		isConciergeDrawerOpen ||
		openConciergeIframe ||
		shouldOpenOurDoctorDrawer ||
		isOdontoTermDrawerOpen ||
		isFiveStarsModalOpen;

	const showWaitingPeriodTutorial = Boolean(
		isInGracePeriod && isWithinEnrollmentPeriod && isQuickWinWaitingPeriodFlagEnabled,
	);

	const isWaitingPeriodTutorialVisible =
		showWaitingPeriodTutorial && isWaitingPeriodTutorialActive;

	const showElectronicBillSection = electronicBillAlertEnabled && !isWaitingPeriodTutorialVisible;

	const shouldBlockTutorialInShortcutList = isAnyHomeModalOpen || isWaitingPeriodTutorialVisible;

	const showRegistrationUpdateSurface = !isWaitingPeriodTutorialVisible;

	const handleCompleteWaitingPeriodTutorial = () => {
		setIsWaitingPeriodTutorialActive(false);
		navigate(HealthCardEnum.HEALTH_CARD);
	};

	const handleFavoritesShortcutTutorialDismiss = () => {
		setIsFavoritesShortcutTutorialActive(false);
	};

	const content = (
		<StyledGrid.Root p={isMobile ? 2 : 4}>
			<StyledGrid.Item sm={12} md={12} lg={8}>
				<Stack gap={4}>
					{isTablet && isNotredameSaude ? (
						<Link to={ServicesEnum.SERVICES}>
							<SearchBar placeholder={searchService} readOnly />
						</Link>
					) : null}
					<SectionContainer
						title={isNotredameSaude ? quickAccess : ''}
						actionText={isNotredameSaude ? seeAll : ''}
						showTitle={isNotredameSaude}
						titleMarginTop={0}
						titleMarginBottom={isNotredameSaude ? 2 : 0}
						handleAction={() => navigate(ServicesEnum.SERVICES)}>
						<ShortcutList
							isHome
							isTutorialBlocked={shouldBlockTutorialInShortcutList}
							onTutorialDismiss={handleFavoritesShortcutTutorialDismiss}
						/>
					</SectionContainer>

					{showDebtSettlementStatement && isLaptop ? <DebtSettlementStatement /> : null}

					{myHospitalizationEnabled ? (
						<Fragment>
							{showMyHospitalizationAlert && isLaptop ? (
								<MyHospitalizationAlert />
							) : null}
						</Fragment>
					) : (
						<Fragment>{showConcierge && isLaptop ? <Concierge /> : null}</Fragment>
					)}

					<Stack direction={isNotredameSaude ? 'column-reverse' : 'column'} gap={3}>
						{bannerHomeEnabled ? bannerComponent : null}

						{!isEarlyAccess ? <NextAppointments /> : null}
					</Stack>

					{contentManagerEnabled && !isNotredameSaude ? (
						<Fragment>{isLaptop ? null : <YouTubeBanners />}</Fragment>
					) : null}
				</Stack>
			</StyledGrid.Item>
			<StyledGrid.Item
				sm={12}
				md={12}
				lg={4}
				style={{
					borderLeft:
						!isLaptop && isNotredameSaude
							? `1px solid ${theme.colors.gray['20Percent']}`
							: 'none',
				}}>
				{shouldOpenOurDoctorDrawer ? (
					<OurDoctorHomeDrawer isOpen={shouldOpenOurDoctorDrawer} />
				) : null}
				<Stack gap={3} marginTop={isLaptop ? 1 : 0} marginLeft={isLaptop ? 0 : 1}>
					{myHospitalizationEnabled ? (
						<Fragment>
							{showMyHospitalizationAlert && !isLaptop ? (
								<MyHospitalizationAlert />
							) : null}
						</Fragment>
					) : (
						<Fragment>{showConcierge && !isLaptop ? <Concierge /> : null}</Fragment>
					)}

					{isLaptop ? null : (
						<Suspense fallback={<Skeleton variant="rounded" height={90} />}>
							<RemindersAndNotifications isInGracePeriod={Boolean(isInGracePeriod)} />
						</Suspense>
					)}

					{isValid ? (
						<UserManualAlert
							dataTagId={LoginEnum.CLICK_ACESSAR_MANUAL}
							type="features"
						/>
					) : null}

					{showDebtSettlementStatement && !isLaptop ? <DebtSettlementStatement /> : null}

					{showFinancial ? <Financial /> : null}

					{showWhoIndicates ? (
						<Stack gap={!isDesktop ? 2 : 1}>
							<Typography
								fontWeight={600}
								variant="body2"
								fontSize={isNotredameSaude ? '1rem' : '0.875rem'}
								color={isNotredameSaude ? 'text.primary' : 'text.secondary'}>
								{referAndEarn}
							</Typography>
							<WhoIndicatesCard
								$showAsBanner={!isMobile && !isDesktop}
								pathParametric={WhoIndicatesTagParametricEnum.HOME}
							/>
						</Stack>
					) : null}

					{contentManagerEnabled && !isNotredameSaude ? (
						<Fragment>
							<BlogBanners />
							{isLaptop ? <YouTubeBanners /> : null}
						</Fragment>
					) : null}
				</Stack>
			</StyledGrid.Item>
		</StyledGrid.Root>
	);

	return (
		<Fragment>
			<MainHeader variant={isTablet || (isTablet && isNotredameSaude) ? 'logo' : 'search'}>
				{isLaptop ? (
					<StyledBox>
						{isTablet &&
						isValid &&
						!isResponsibleWithoutBenefits &&
						!isNotredameSaude ? (
							<Link to={ServicesEnum.SERVICES}>
								<SearchBar placeholder={searchService} readOnly />
							</Link>
						) : null}

						<RemindersAndNotifications isInGracePeriod={Boolean(isInGracePeriod)} />
					</StyledBox>
				) : null}
			</MainHeader>
			<PageWrapperLayout title={title}>
				{isNotredameSaude ? (
					<StyledBox
						$backgroundColor={
							isNotredameSaude ? theme.palette.primary.main : 'transparent'
						}
						p={'0px !important'}>
						<StyledPortalHomeCard>{content}</StyledPortalHomeCard>
					</StyledBox>
				) : (
					content
				)}

				<Box mt="auto">
					<MainFooter />
				</Box>
				{isResponsibleWithoutBenefits ? null : <FiveStars hideComponent={hideFiveStars} />}

				{isTelemedicineError ? (
					<ModalError
						open={isTelemedicineError}
						onClose={() => setIsTelemedicineError(false)}
						headerTitle={modalErrorTitle}
						title={subtitle}
						isFullScreen={false}
						maxWidth={420}
						description={description}
						firstButton={{
							title: backAction,
							handleClick: () => setIsTelemedicineError(false),
							variant: 'outlined',
						}}
						secondButton={{
							title: tryAgainBtn,
							handleClick: () => {
								setIsTelemedicineError(false);
								handleEmergencyTelehealthClick();
							},
							variant: 'contained',
						}}
					/>
				) : null}

				<ConciergeDrawer />
				{showElectronicBillSection ? <ElectronicBillSection /> : null}

				{isGndiMinas && <CongenereDrawer />}

				<OdontoTermDrawer />

				{showRegistrationUpdateSurface ? (
					<RegistrationUpdateSurface
						isAnyHomeModalOpen={isAnyHomeModalOpen || isFavoritesShortcutTutorialActive}
					/>
				) : null}

				{myHospitalizationEnabled ? null : (
					<Fragment>
						{showConcierge && conciergeUrl ? (
							<IFrameModal
								openIframe={openConciergeIframe}
								setOpenIframe={setOpenConciergeIframe}
								title={conciergeTitle}
								url={conciergeUrl}
								handleBack={handleCloseIframe}
							/>
						) : null}
					</Fragment>
				)}

				{showWaitingPeriodTutorial ? (
					<WaitingPeriodTutorial onComplete={handleCompleteWaitingPeriodTutorial} />
				) : null}
			</PageWrapperLayout>
		</Fragment>
	);
}
